<template>
  <div class="preview-video" v-loading="loading" ref="video-container">
    <template v-if="isAvailable">
      <video 
        class="preview-video__view"
        preload="auto"
        ref="video"
        :autoplay="autoPlay"
        :style="styles"
        @click="handleTogglePlay"
        @loadedmetadata="loadedMetaDataEvent">
        <source v-if="previewSrc" :src="previewSrc" webkit-playsinline playsinline x-webkit-airplay x5-playsinline @error="handleLoadError" />
      </video>
      <div class="preview-video__contrl">
        <div class="preview-video__contrl-inner">
          <PreviewPlay ref="play" ref-name="video" />
          <PreviewTimer />
          <PreviewFullscreen ref-name="video" />
          <Download :file="previewFile" />
          <PreviewRotate @change="handleChangeRotate" />
        </div>
      </div>
      <div v-if="isMobile" class="preview-video__footer" ref="footer">
        <div class="preview-action">
          <Download :file="previewFile" />
          <PreviewRotate @change="handleChangeRotate" />
        </div>
        <div class="app-download">
          <AppLink v-if="platform.isIos" type="apple" class="item" />
          <AppLink v-else type="google" class="item" />
        </div>
      </div>
    </template>    
    <template v-else>
      <div class="shares-unavaiable">
        <span class="shares-unavaiable__text" v-text="$t('preview.msg_expired')"></span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Download from '@/components/download'
import AppLink from '@/components/app-link'
import PreviewRotate from '../components/rotate'
import PreviewPlay from '../components/play'
import PreviewTimer from '../components/timer'
import PreviewFullscreen from '../components/fullscreen'
import { addEvent, removeEvent } from '@/utils/event'

export default {
  name: 'PreviewVideo',
  components: {
    Download,
    AppLink,
    PreviewRotate,
    PreviewPlay,
    PreviewTimer,
    PreviewFullscreen
  },
  props: {
    autoPlay: {
      type: Boolean,
      default: true
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rotate: 0,
      loading: true,
      isAvailable: true
    }
  },
  computed: {
    ...mapGetters([
      'platform',
      'previewFile',
      'playing'
    ]),
    previewSrc() {
      const { shareId } = this.$route.params
      return this.previewFile ? `/na-api/shares/${shareId}/files/${this.previewFile.id}/original/` : ''
    },
    styles() {
      return {
        transform: `rotate(${this.rotate})`
      }
    }
  },
  watch: {
    previewSrc() {
      this.init()
    }
  },
  mounted() {
    this.init()
    addEvent(this.$refs.video, 'canplay', this.handleCanplay)
  },
  beforeDestroy() {
    removeEvent(this.$refs.video, 'canplay', this.handleCanplay)
  },
  methods: {
    init() {
      this.$topic.publish('preview.reset')
      this.$nextTick(() => {
        this.$refs.video && this.$refs.video.load()
      })
    },
    loadedMetaDataEvent(e){
      // When loaded the meta data of the video we look for the width and height of the medio
      const {videoWidth: width, videoHeight: height} = e.target

      // If the media has no height and width, that means our video can't display on this browser 
      if(width <= 0 && height <= 0) {
        // So we show that to the user
        this.$message.error(this.$t('preview.msg_video_not_supported'))
      }
    },
    async handleCanplay() {
      const { video } = this.$refs
      this.loading = false

      if (video) {
        try {
          await (this.autoPlay ? video.play() : video.pause())
        } catch(e) {
          if (this.playing) {
            this.$store.commit('UPDATE_PLAYING_STATUS', false)
          }
        }
      }
    },
    handleChangeRotate(rotate) {
      this.rotate = rotate
    },
    handleTogglePlay() {
      this.$refs.play.handleClick()
    },
    handleLoadError() {
      this.loading = false
      this.isAvailable = false
    }
  }
}
</script>

<style lang="less">
  @import url('~@/styles/var.less');
  video::-webkit-media-controls {
    display: none !important;
  }
  video::-moz-media-controls {
    display: none !important;
  }
  video::-o-media-controls {
    display: none !important;
  }
  video::media-controls {
    display: none !important;
  }
  .preview-video {
    position: relative;
    width: 100%;
    height: 100%;
    & &__view {
      width: 100%;
      height: 100%;
    }
    & &__contrl {
      position: absolute;
      left: 50%;
      bottom: 40px;
      transform: translateX(-50%);
      width: 625px;
      height: 48px;
      max-width: 95%;
      overflow: hidden;
      &-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px;
        background-color: var(--background-contrl, @background-contrl);
        border-radius: 16px;
        overflow: hidden;
      }
    }
  }

  @media screen and (max-width: 1024px), screen and (max-height: 576px) {
    .preview-video {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-direction: column;
      & &__view {
        height: calc(100% - 86px);
      }      
      & &__footer {
        flex: 0 0 86px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100vw;
        padding: 0 16px 0 8px;
        .preview-action {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
      }
      & &__contrl {
        bottom: 126px;
        .let-download,
        .preview-rotate {
          display: none;
        }
      }
    }
  }
</style>